import React, { useMemo } from "react";
import CountUp from "react-countup";
import BaseChart from "../../components/ui/BaseChart";

const CARDS = [
  {
    key: "totalWorkOrders",
    label: "Total Work Orders",
    subLabel: "This month",
    icon: "fa-solid fa-clipboard-list",
    color: "#0F40AE",
    bg: "#EEF2FF",
  },
  {
    key: "today",
    label: "Today",
    subLabel: "Today",
    icon: "fa-light fa-calendar-day",
    color: "#7C3AED",
    bg: "#F5F3FF",
  },
  {
    key: "assigned",
    label: "Assigned",
    subLabel: "Current",
    icon: "fa-solid fa-circle-half-stroke",
    color: "#0891B2",
    bg: "#ECFEFF",
  },
  {
    key: "inProgress",
    label: "In Progress",
    subLabel: "Current",
    icon: "fa-jelly fa-regular fa-chart-bar",
    color: "#f7ad00",
    bg: "#ffedcdc1",
  },
  {
    key: "completed",
    label: "Completed",
    subLabel: "This month",
    icon: "fa-solid fa-circle-check",
    color: "#16A34A",
    bg: "#F0FDF4",
  },
  {
    key: "cancelled",
    label: "Cancelled",
    subLabel: "This month",
    icon: "fa-solid fa-ban",
    color: "#DC2626",
    bg: "#FEF2F2",
    // Caption under the headline count: cancellations the client has actioned
    // (customerSubStatus CANCELLED / customerStatus CANCELLED_PAYABLE).
    extraKey: "customerCancelled",
    extraLabel: "Payable",
  },
];

const Sparkline = ({ data, color }) => {
  const options = useMemo(
    () => ({
      chart: {
        type: "area",
        height: 52,
        margin: [4, 0, 4, 0],
        backgroundColor: "transparent",
        animation: false,
      },
      title: { text: null },
      xAxis: { visible: false },
      yAxis: { visible: false, min: 0 },
      legend: { enabled: false },
      tooltip: { enabled: false },
      plotOptions: {
        area: {
          marker: { enabled: false },
          lineWidth: 2,
          lineColor: color,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, `${color}55`],
              [1, `${color}00`],
            ],
          },
          states: { hover: { enabled: false } },
        },
      },
      series: [{ name: "Trend", data: data?.length ? data : Array(12).fill(0) }],
    }),
    [data, color],
  );

  return <BaseChart options={options} style={{ width: "100%", height: "52px" }} />;
};

const TrendBadge = ({ pct }) => {
  if (pct === undefined || pct === null) return null;
  const up = pct >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
        up ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"
      }`}
    >
      <i className={`fa-solid ${up ? "fa-arrow-up" : "fa-arrow-down"} text-[9px]`} />
      {Math.abs(pct)}%
    </span>
  );
};

const KpiCards = ({ data }) => (
  <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
    {CARDS.map((card) => {
      const kpi = data?.[card.key];
      return (
        <div
          key={card.key}
          className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3"
        >
          {/* Header row */}
          <div className="flex items-start justify-between">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: card.bg }}
            >
              <i className={`${card.icon} text-sm`} style={{ color: card.color }} />
            </div>
            <TrendBadge pct={kpi?.changePercent} />
          </div>

          {/* Value */}
          <div>
            <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide leading-none mb-1">
              {card.label}
            </p>
            <p className="text-3xl font-bold text-gray-800 leading-none">
              {kpi ? <CountUp start={0} end={kpi.value} duration={1.5} separator="," /> : "—"}
            </p>
          </div>

          {/* Sparkline */}
          <Sparkline data={kpi?.sparkline} color={card.color} />

          {/* Footer comparison */}
          <div className="mt-auto flex flex-col gap-1 text-[11px] text-gray-400 border-t border-gray-50 pt-2">
            <div className="flex items-center justify-between leading-none">
              <span>{card.subLabel}</span>
              {kpi?.previousValue !== undefined && (
                <span>
                  Prev:{" "}
                  <span className="font-semibold text-gray-600">
                    <CountUp start={0} end={kpi.previousValue} duration={1.5} separator="," />
                  </span>
                </span>
              )}
            </div>
            {/* Rendered on every card — blank where there is no extra metric —
                so all six keep the same height and stay aligned. */}
            <div className="flex items-center justify-end leading-none">
              {card.extraKey && kpi?.[card.extraKey] !== undefined ? (
                <span>
                  {card.extraLabel}:{" "}
                  <span className="font-semibold text-gray-600">
                    <CountUp start={0} end={kpi[card.extraKey]} duration={1.5} separator="," />
                  </span>
                </span>
              ) : (
                "\u00A0"
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

export default KpiCards;
